import {
  Button,
  Card,
  Divider,
  Flex,
  Input,
  Popconfirm,
  Table,
  Typography,
  message,
  AutoComplete,
  Modal,
  Select,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import { CandidateInterface } from "api/users/Candidates/dto";
import { useFilterPagination } from "hooks/useFilterPagination";
import { APPLICATION_STATUS } from "enum/job-status-type";
import { useGetJobsByRecruiter } from "api/jobs/useGetJobsByRecruiter";
import { useUpdateCandidateApplication } from "api/users/Candidates/useUpdateCandidateStatus";
import { storage } from "utils/storage";
import sendMessage from "api/messages/useSendMessage";
import CandidateProfile from "views/Profile/CandidateProfile";

// Keep Firebase ONLY for CSV + resume download
import { useGetAllResume } from "api/users/Candidates/useGetAllResume";
import { useExtractAllDataCSV } from "api/users/Candidates/useExtractAllDataCSV";

// Meili search hooks/types
import { useSearchCandidates } from "api/users/Candidates/useSearchCandidates";
import { useQuestionKeys } from "api/users/Candidates/useQuestionKeys";
import { useFacets } from "api/users/Candidates/useFacets";
import type { FilterRow } from "types/meili";

const { Title } = Typography;

type ApplicationPayload = {
  id: string;
  applicantId: string;
  status: string;
  jobId: string;
  jobTitle: string;
  companyName: string;
};

// --- ADDITION: Add an optional 'error' field to the FilterRow type ---
type FilterRowState = FilterRow & { error?: string };

const CandidatesList = () => {
  const user = storage.getUserFromStorage();

  // ---------------- Hooks ----------------
  const { queryParamaters, onTableChange } =
    useFilterPagination();

  const [applicationPayload, setApplicationPayload] =
    useState<ApplicationPayload | null>(null);

  const [selectedJob, setSelectedJob] = useState<string>();
  const [searchValue, setSearchValue] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<Record<string, string>>(
    {}
  );

  const [selectedCandidateInfo, setSelectedCandidateInfo] = useState<
    any | null
  >(null);
  const [selectedCoverLetter, setSelectedCoverLetter] = useState<string>();
  const [selectedAnswers, setSelectedAnswers] =
    useState<Record<string, string | boolean>>();

  // Meili global search + filter builder (DEFAULT)
  const [q, setQ] = useState("");
  const [filterRows, setFilterRows] = useState<FilterRowState[]>([]);

  const { data: recruiterJobList } = useGetJobsByRecruiter({
    recruiterId: user.uid,
  });

  // Firebase-only features
  const { mutate: downloadALLResume, isPending: isDownloadingResumes } = useGetAllResume();
  const { mutate: handleExtractCSV, isPending: isCSVLoading } =
    useExtractAllDataCSV();

  // Dynamic question fields + facets (enabled by jobId)
  const { isFetching: isQFetch } =
    useQuestionKeys(selectedJob);
  
  const builtinFields = [
    { label: "Status", value: "status" },
    { label: "Gender", value: "applicant_info_gender" },
    { label: "Age", value: "applicant_info_age" },
    { label: "Languages", value: "applicant_info_languages" },
    { label: "Email", value: "applicant_info_email" },
    { label: "Phone", value: "applicant_info_phoneNumber" },
    {
      label: "Province (perm.)",
      value: "applicant_info_permanentAddress_province",
    },
    {
      label: "District (perm.)",
      value: "applicant_info_permanentAddress_district",
    },
    {
      label: "Municipality (perm.)",
      value: "applicant_info_permanentAddress_municipality",
    },
    {
      label: "Province (temp.)",
      value: "applicant_info_temporaryAddress_province",
    },
    {
      label: "District (temp.)",
      value: "applicant_info_temporaryAddress_district",
    },
    {
      label: "Municipality (temp.)",
      value: "applicant_info_temporaryAddress_municipality",
    },
    { label: "AppliedAt (seconds)", value: "appliedAt__seconds" },
    { label: "Experience (years)", value: "totalExperienceYears" },
  ];

  const facetFields = [
    "status",
    "applicant_info_gender",
    "applicant_info_languages",
    "applicant_info_permanentAddress_province",
    "applicant_info_permanentAddress_district",
    "applicant_info_permanentAddress_municipality",
    "applicant_info_temporaryAddress_province",
    "applicant_info_temporaryAddress_district",
    "applicant_info_temporaryAddress_municipality",
  ];

  const { data: facetDist = {} } = useFacets(selectedJob, facetFields);
  const facetOptions: Record<string, string[]> = Object.fromEntries(
    Object.entries(facetDist as any).map(([k, v]) => [k, Object.keys(v as any)])
  );

  // Meili search params (DEFAULT datasource)
  const meiliParams = selectedJob
    ? {
        jobId: selectedJob,
        q,
        page: queryParamaters.page || 1,
        pageSize: queryParamaters.pageSize || 10,
        filters: [...filterRows],
      }
    : null;

  const { data: meiliRes, isFetching: isMeiliFetching } = useSearchCandidates(
    meiliParams as any
  );

  // ---------------- Effects ----------------
  useEffect(() => {
    if (meiliRes?.data) {
      const newMap: Record<string, string> = {};
      (meiliRes.data as any[]).forEach((c) => (newMap[c.id] = c.status || ""));
      setSelectedStatus((prev) => {
        return JSON.stringify(prev) === JSON.stringify(newMap) ? prev : newMap;
      });
    }
  }, [meiliRes]);

  useEffect(() => {
    setQ("");
    setFilterRows([]);
  }, [selectedJob]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case APPLICATION_STATUS.REJECTED:
        return "red";
      case APPLICATION_STATUS.APPROVED:
        return "green";
      case APPLICATION_STATUS.SHORT_LISTED:
        return "blue";
      default:
        return "black";
    }
  };

  const updateJobApplicationMutation = useUpdateCandidateApplication();

  const confirm = async () => {
    if (!applicationPayload) {
      message.error("Application details not found.");
      return;
    }
    try {
      const { applicantId, jobId, id, status, jobTitle, companyName } =
        applicationPayload;
      if (status === APPLICATION_STATUS.APPROVED) {
        const hiringMessage = `Congratulations! You have been hired for ${jobTitle} at ${companyName}`;
        await sendMessage({
          sender: user.uid,
          recipient: applicantId,
          jobId,
          companyName,
          jobTitle,
          status: "unread",
          text: hiringMessage,
        });
      }
      const payload = { id, jobId, applicantId, status };
      updateJobApplicationMutation.mutate(payload, {
        onSuccess: () => {
          setSelectedStatus((prev) => ({
            ...prev,
            [payload.id]: payload.status,
          }));
          message.success(`Candidate status updated successfully.`);
        },
      });
    } catch (err: any) {
      message.error(err.message);
    }
  };

  const columns = [
    {
      title: "Candidate",
      key: "candidate",
      render: (_: any, record: CandidateInterface) => (
        <div style={{ textTransform: "capitalize" }}>
          {record.applicant_info?.firstName} {record.applicant_info?.lastName}
        </div>
      ),
    },
    {
      title: "Job Title",
      key: "jobTitle",
      render: (_: any, record: CandidateInterface) => (
        <div>{record.job?.jobTitle}</div>
      ),
    },
    {
      title: "Email",
      key: "email",
      render: (_: any, record: CandidateInterface) => (
        <div>{record.applicant_info?.email}</div>
      ),
    },
    {
      title: "Phone Number",
      key: "phoneNumber",
      render: (_: any, record: CandidateInterface) => (
        <div>{record.applicant_info?.phoneNumber}</div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: any) => {
        if (!record.applicant_info)
          return <Button disabled>Invalid Data</Button>;
        const status = selectedStatus[record.id] || "";
        const actionPayloadBase = {
          id: record.id,
          applicantId: record.applicant_info.uid,
          jobId: record.jobId,
          jobTitle: record.job.jobTitle,
          companyName: record.job.companyName,
        };
        return (
          <Flex gap={"small"}>
            {status !== APPLICATION_STATUS.SHORT_LISTED &&
              status !== APPLICATION_STATUS.APPROVED &&
              status !== APPLICATION_STATUS.REJECTED && (
                <Popconfirm
                  title="Shortlist this candidate?"
                  onConfirm={confirm}
                  okButtonProps={{ style: { backgroundColor: "#22AD5C" } }}
                >
                  <Button
                    style={{ backgroundColor: "#FBBF24" }}
                    type="primary"
                    onClick={() =>
                      setApplicationPayload({
                        ...actionPayloadBase,
                        status: APPLICATION_STATUS.SHORT_LISTED,
                      })
                    }
                  >
                    Shortlist
                  </Button>
                </Popconfirm>
              )}
            {status === APPLICATION_STATUS.SHORT_LISTED && (
              <Popconfirm
                title="Hire this candidate?"
                onConfirm={confirm}
                okButtonProps={{ style: { backgroundColor: "#22AD5C" } }}
              >
                <Button
                  type="primary"
                  className="bg-primary"
                  onClick={() =>
                    setApplicationPayload({
                      ...actionPayloadBase,
                      status: APPLICATION_STATUS.APPROVED,
                    })
                  }
                >
                  Hire
                </Button>
              </Popconfirm>
            )}
            {status !== APPLICATION_STATUS.APPROVED &&
              status !== APPLICATION_STATUS.REJECTED && (
                <Popconfirm
                  title="Reject this candidate?"
                  onConfirm={confirm}
                  okButtonProps={{ style: { backgroundColor: "#22AD5C" } }}
                >
                  <Button
                    onClick={() =>
                      setApplicationPayload({
                        ...actionPayloadBase,
                        status: APPLICATION_STATUS.REJECTED,
                      })
                    }
                  >
                    Reject
                  </Button>
                </Popconfirm>
              )}
            <Button
              type="primary"
              onClick={() => {
                setSelectedCandidateInfo(record);
                setSelectedCoverLetter(record.coverLetter);
                setSelectedAnswers(record.questionAnswers);
              }}
            >
              View Details
            </Button>
          </Flex>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, record: CandidateInterface) => {
        const status = selectedStatus[record.id] || "";
        return <div style={{ color: getStatusColor(status) }}>{status}</div>;
      },
    },
  ];

  const filteredJobs = recruiterJobList?.data
    ?.filter((job) =>
      job.jobTitle.toLowerCase().includes(searchValue.toLowerCase())
    )
    .map((job) => ({
      value: job.id,
      label: `${job.jobTitle} (${job.status})`,
    }));

  const handleTablePagination = (page: number, pageSize?: number) => {
    onTableChange(page, pageSize || queryParamaters.pageSize || 10);
  };

  return (
    <>
      <Modal
        title="Candidate Profile"
        open={!!selectedCandidateInfo}
        onCancel={() => setSelectedCandidateInfo(null)}
        footer={null}
        width={1100}
      >
        {selectedCandidateInfo && (
          <CandidateProfile
            profile={selectedCandidateInfo}
            coverLetter={selectedCoverLetter}
            questionAnswers={selectedAnswers}
          />
        )}
      </Modal>

      <div className="bg-background">
        <div className="max-w-8xl mx-auto py-12">
          <Card>
            <Title level={2}>Candidates List</Title>

            <Flex
              justify="space-between"
              align="center"
              wrap="wrap"
              gap="middle"
            >
              <AutoComplete
                value={searchValue}
                onChange={setSearchValue}
                onSelect={(jobId, option) => {
                  setSelectedJob(jobId as string);
                  setSearchValue((option as any).label.split(" (")[0]);
                }}
                options={filteredJobs}
                style={{ width: 320, marginBottom: 16 }}
              >
                <Input.Search placeholder="Search for one of your jobs..." />
              </AutoComplete>
            </Flex>

            <Divider />

            {!selectedJob ? (
              <div style={{ textAlign: "center", padding: 50 }}>
                <Title level={4} type="secondary">
                  Please select one of your jobs to display the candidates
                </Title>
              </div>
            ) : (
              <>
                <Flex
                  justify="space-between"
                  align="center"
                  wrap="wrap"
                  style={{ marginBottom: 16 }}
                >
                  <Flex align="center" gap="small" style={{ flexWrap: "wrap" }}>
                    <Input.Search
                      placeholder="Search everything (name, email, phone, addresses, answers...)"
                      allowClear
                      enterButton="Search"
                      style={{ width: 420 }}
                      onSearch={setQ}
                      onChange={(e) => setQ(e.target.value)}
                      value={q}
                      loading={isQFetch}
                    />
                  </Flex>

                  <Flex gap="middle">
                    <Button
                      onClick={() => handleExtractCSV(selectedJob)}
                      loading={isCSVLoading}
                    >
                      Extract CSV
                    </Button>
                    <Button
                      onClick={() => downloadALLResume(selectedJob)}
                      loading={isDownloadingResumes}
                      type="primary"
                    >
                      Download All Resumes
                    </Button>
                  </Flex>
                </Flex>

                <div style={{ marginBottom: 12 }}>
                  <Flex align="center" gap="small" style={{ marginBottom: 8 }}>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setFilterRows((r) => [
                          ...r,
                          {
                            field: "status",
                            op: "=",
                            value: "",
                          },
                        ]);
                      }}
                    >
                      Add filter
                    </Button>
                  </Flex>

                  {filterRows.map((row, idx) => {
                    const allFieldOptions = builtinFields.map((b) => ({
                      label: b.label,
                      value: b.value,
                    }));
                    
                    const field = row.field as string;
                    const isNumericField = field.includes('age') || field.includes('phoneNumber') || field.includes('seconds') || field.includes('totalExperienceYears');
                    
                    const numericOps = [
                      { label: "=", value: "=" }, { label: "!=", value: "!=" },
                      { label: ">=", value: ">=" }, { label: "<=", value: "<=" },
                      { label: ">", value: ">" }, { label: "<", value: "<" },
                    ];
                    const textOps = [ { label: "=", value: "=" }, { label: "!=", value: "!=" }];
                    const availableOps = isNumericField ? numericOps : textOps;
                    
                    const possibleValues = facetOptions[field];

                    const updateRow = (patch: Partial<FilterRowState>) =>
                      setFilterRows((rows) =>
                        rows.map((r, i) =>
                          i === idx ? ({ ...r, ...patch } as FilterRowState) : r
                        )
                      );

                    return (
                      <Flex
                        key={idx}
                        gap="small"
                        align="center"
                        style={{ marginBottom: 8, flexWrap: "wrap" }}
                      >
                        <Select
                          value={field}
                          onChange={(v) => {
                            const isNewFieldNumeric = v.includes('age') || v.includes('phoneNumber') || v.includes('seconds');
                            const newOp = isNewFieldNumeric ? '>=' : '=';
                            updateRow({ field: v as any, value: "", op: newOp, error: undefined });
                          }}
                          style={{ width: 260 }}
                          options={allFieldOptions}
                          showSearch
                          optionFilterProp="label"
                        />
                        <Select
                          value={row.op as any}
                          onChange={(v) => updateRow({ op: v as any })}
                          style={{ width: 130 }}
                          options={availableOps}
                        />

                        {/* --- ADDITION: Final version of the smart input with validation styling --- */}
                        {possibleValues ? (
                          <Select
                            style={{ width: 320 }}
                            showSearch
                            allowClear
                            placeholder="Type or select..."
                            options={possibleValues.map((v) => ({
                              label: String(v),
                              value: v,
                            }))}
                            value={row.value as any}
                            onChange={(v) => updateRow({ value: v })}
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          />
                        ) : (
                          <Input
                            style={{ width: 320 }}
                            placeholder={isNumericField ? "Numbers only..." : "Enter a value..."}
                            status={row.error ? 'error' : ''} // This turns the input red
                            value={row.value as any}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (isNumericField) {
                                if (raw === '' || /^\d+$/.test(raw)) {
                                  // If valid number or empty, update value and clear error
                                  updateRow({ value: raw === '' ? '' : Number(raw), error: undefined });
                                } else {
                                  // If invalid, keep the raw text but set an error
                                  updateRow({ value: raw, error: 'Invalid number' });
                                }
                              } else {
                                updateRow({ value: raw });
                              }
                            }}
                          />
                        )}

                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() =>
                            setFilterRows((r) =>
                              r.filter((_, i) => i !== idx)
                            )
                          }
                        />
                      </Flex>
                    );
                  })}
                </div>

                <Table<CandidateInterface>
                  rowKey="id"
                  dataSource={(meiliRes?.data as any) || []}
                  columns={columns}
                  loading={isMeiliFetching}
                  scroll={{ x: true }}
                  pagination={{
                    total: meiliRes?.totalCount,
                    current: meiliRes?.page,
                    pageSize: meiliRes?.pageSize,
                    onChange: handleTablePagination,
                    position: ["bottomCenter"],
                  }}
                />
              </>
            )}
          </Card>
        </div>
      </div>
    </>
  );
};

export default CandidatesList;